/**
 * CONNECT STORE PAGE
 *
 * Allows authenticated users to connect their Shopify stores.
 * Uses Shopify OAuth to get store access tokens.
 */

import { useNavigate } from 'react-router-dom';
import {
  Page,
  Card,
  BlockStack,
  Text,
  Banner,
  InlineStack,
  Box,
  ResourceList,
  ResourceItem,
  Badge,
  EmptyState,
} from '@shopify/polaris';
import { WarehouseSetup } from '../components/company/WarehouseSetup';
import { ConnectShopify } from '../components/shopify/ConnectShopify';
import { connectionErrors } from '../../../packages/shared/shopify-connect/contract';
import { useAuth } from '../App';

export default function ConnectStore() {
  const { user, shops, currentShop, setCurrentShop, currentOrganization } = useAuth();
  const navigate = useNavigate();

  const handleSelectShop = (shop: typeof shops[0]) => {
    setCurrentShop(shop);
    navigate('/');
  };

  return (
    <Page
      title="Warehouse accounts and Shopify"
      backAction={shops.length > 0 ? { content: 'Dashboard', url: '/' } : undefined}
    >
      <BlockStack gap="600">
        {new URLSearchParams(window.location.search).get('connected')==='1'&&<Banner tone="success">Shopify connection setup returned. Check the saved connection and current store access in Settings.</Banner>}
        {new URLSearchParams(window.location.search).has('error')&&<Banner tone="warning">{connectionErrors[new URLSearchParams(window.location.search).get('error')||'']||'The Shopify connection was not completed. Review it again.'}</Banner>}
        {/* Connected Stores */}
        {shops.length > 0 && (
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Your warehouse accounts
              </Text>

              <ResourceList
                resourceName={{ singular: 'store', plural: 'stores' }}
                items={shops}
                renderItem={(shop) => (
                  <ResourceItem
                    id={shop.id}
                    onClick={() => handleSelectShop(shop)}
                    accessibilityLabel={`Select ${shop.name || shop.domain}`}
                  >
                    <InlineStack align="space-between" blockAlign="center">
                      <InlineStack gap="400" blockAlign="center">
                        <Box>
                          <Text as="span" variant="bodyMd" fontWeight="semibold">
                            {shop.name || shop.domain}
                          </Text>
                          <Text as="p" variant="bodySm" tone="subdued">
                            {shop.domain}
                          </Text>
                        </Box>
                      </InlineStack>
                      <InlineStack gap="200">
                        <Badge tone={shop.role === 'admin' ? 'success' : 'info'}>
                          {shop.role}
                        </Badge>
                        {currentShop?.id === shop.id && (
                          <Badge tone="attention">Current</Badge>
                        )}
                      </InlineStack>
                    </InlineStack>
                  </ResourceItem>
                )}
              />
            </BlockStack>
          </Card>
        )}

        {currentOrganization&&['owner','admin'].includes(currentOrganization.role)&&<WarehouseSetup key={`${user?.id}:${currentOrganization.id}`} />}
        <ConnectShopify key={`${user?.id}:${currentOrganization?.id}:${currentShop?.id}`} />

        {/* Help Section */}
        <Card>
          <BlockStack gap="300">
            <Text as="h3" variant="headingSm">
              How to find your store domain
            </Text>
            <BlockStack gap="200">
              <Text as="p" variant="bodyMd">
                1. Log into your Shopify admin
              </Text>
              <Text as="p" variant="bodyMd">
                2. Look at the URL in your browser
              </Text>
              <Text as="p" variant="bodyMd">
                3. Your domain is the part before .myshopify.com
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                Example: If your admin URL is https://mystore.myshopify.com/admin, enter "mystore"
              </Text>
            </BlockStack>
          </BlockStack>
        </Card>

        {/* Empty State for first-time users */}
        {shops.length === 0 && (
          <Card>
            <EmptyState
              heading="Ready to get started?"
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <Text as="p" variant="bodyMd">
                With a warehouse account, you can:
              </Text>
              <BlockStack gap="200">
                <Text as="p" variant="bodyMd">
                  - Manage delivery date rules and blocked dates
                </Text>
                <Text as="p" variant="bodyMd">
                  - Configure shipping zones and capacities
                </Text>
                <Text as="p" variant="bodyMd">
                  - Track inventory across multiple warehouses
                </Text>
                <Text as="p" variant="bodyMd">
                  - Generate pick lists and manage orders
                </Text>
              </BlockStack>
            </EmptyState>
          </Card>
        )}

        {/* Info for staff members */}
        {shops.length === 0 && (
          <Card>
            <BlockStack gap="300">
              <Text as="h3" variant="headingSm">
                Are you a team member?
              </Text>
              <Text as="p" variant="bodyMd" tone="subdued">
                If you're joining an existing team, ask your organisation owner or administrator to
                invite you. They can send an invitation from <strong>Settings → Team Members</strong>.
              </Text>
              <Text as="p" variant="bodyMd" tone="subdued">
                Once invited, you'll receive an email with a link to join the organisation and access
                the connected stores.
              </Text>
            </BlockStack>
          </Card>
        )}
      </BlockStack>
    </Page>
  );
}
